import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Target, Hash } from "lucide-react";
import { RollingWorkout } from "@/components/workout/RollingWorkout";
import { DateSumPanel } from "@/components/workout/DateSumPanel";
import { MirrorPanel } from "@/components/workout/MirrorPanel";
import { HitsPanel } from "@/components/workout/HitsPanel";
import { HistoryPanel } from "@/components/workout/HistoryPanel";
import { UserMenu } from "@/components/UserMenu";
import { UpgradeNudge } from "@/components/UpgradeNudge";
import { useAuth } from "@/contexts/AuthContext";
import {
  parseSeed,
  buildRollingColumns,
  computeDateSum,
  buildCandidateHits,
  luckyPick,
  todayISO,
  formatDateLong,
} from "@/lib/lotteryLogic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios sends cookies for auth
axios.defaults.withCredentials = true;

const DEFAULT_SEED = "2450";

export default function Workspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("pick4"); // pick2 | pick3 | pick4
  const [date, setDate] = useState(todayISO());
  const [seedInput, setSeedInput] = useState(DEFAULT_SEED);

  const seedLength = 4; // grid is always 4-wide; "2450" style workout
  const targetLength = mode === "pick4" ? 4 : mode === "pick3" ? 3 : 2;
  const seedDigits = useMemo(() => parseSeed(seedInput, seedLength), [seedInput]);
  const columns = useMemo(() => buildRollingColumns(seedDigits), [seedDigits]);
  const dateSum = useMemo(() => computeDateSum(date), [date]);
  const candidates = useMemo(
    () => buildCandidateHits(seedDigits, dateSum.sumDigit, targetLength),
    [seedDigits, dateSum, targetLength],
  );

  // Universal coverage: how many unique combos the current workout reaches
  // across lotto (2), pick-3 and pick-4.
  const coverage = useMemo(() => {
    const two = buildCandidateHits(seedDigits, dateSum.sumDigit, 2);
    const three = buildCandidateHits(seedDigits, dateSum.sumDigit, 3);
    const four = buildCandidateHits(seedDigits, dateSum.sumDigit, 4);
    return {
      two: new Set(two.hits.map((h) => h.combo)).size,
      three: new Set(three.hits.map((h) => h.combo)).size,
      four: new Set(four.hits.map((h) => h.combo)).size,
    };
  }, [seedDigits, dateSum]);

  // mirrorFlags[col][row] = bool. Reset when seed/mode changes.
  const [mirrorFlags, setMirrorFlags] = useState([]);
  useEffect(() => {
    setMirrorFlags(columns.map((c) => c.map(() => false)));
  }, [columns.length, seedInput, mode]);

  const [activePair, setActivePair] = useState(null);
  const [globalMirrorOn, setGlobalMirrorOn] = useState(false);
  const [starred, setStarred] = useState([]);
  const [luckyNumber, setLuckyNumber] = useState("");

  const [workouts, setWorkouts] = useState([]);

  const toggleCell = (c, r) => {
    setMirrorFlags((prev) => {
      const next = prev.map((col) => col.slice());
      next[c][r] = !next[c][r];
      return next;
    });
  };

  const toggleStar = (n) => {
    setStarred((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  };

  const doLuckyPick = () => {
    const n = luckyPick(seedDigits, dateSum.sumDigit, targetLength);
    setLuckyNumber(n);
  };

  // ---------- History ----------
  const fetchWorkouts = async () => {
    if (!user) {
      setWorkouts([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/workouts`);
      setWorkouts(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    fetchWorkouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const saveWorkout = async () => {
    if (!user) {
      toast.message("Sign in to save workouts", {
        description: "Create a free account to keep your history across devices.",
        action: { label: "Sign in", onClick: () => navigate("/login", { state: { next: "/" } }) },
      });
      return;
    }
    try {
      await axios.post(`${API}/workouts`, {
        date,
        mode,
        seed: seedDigits.join(""),
        date_sum: dateSum.sumDigit,
        starred_hits: starred,
      });
      toast.success(`Workout saved for ${formatDateLong(date)}`);
      fetchWorkouts();
    } catch (e) {
      toast.error("Save failed");
      console.error(e);
    }
  };

  const openWorkout = (w) => {
    setMode(w.mode);
    setDate(w.date);
    setSeedInput(w.seed);
    setStarred(w.starred_hits || []);
    setActivePair(null);
    setGlobalMirrorOn(false);
    setLuckyNumber("");
    toast.message(`Loaded ${formatDateLong(w.date)}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteWorkout = async (id) => {
    try {
      await axios.delete(`${API}/workouts/${id}`);
      toast.success("Deleted");
      fetchWorkouts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const setDraw = async (id, drawn) => {
    try {
      await axios.patch(`${API}/workouts/${id}`, { actual_draw: drawn || null });
      toast.success("Draw saved");
      fetchWorkouts();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0b0f17]">
      {/* Hero backdrop */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[320px] bg-cover bg-center opacity-[0.09] pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1666071083408-a7acb1a87e5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxsb3R0ZXJ5JTIwZ29sZGVuJTIwbWF0cml4JTIwYWJzdHJhY3QlMjBudW1iZXJzJTIwdGV4dHVyZXxlbnwwfHx8fDE3OTExNjI5MzR8MA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[320px] bg-gradient-to-b from-transparent via-[#0b0f17]/60 to-[#0b0f17] pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 space-y-8">
        {/* HEADER */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-amber-400 font-mono-digit text-xs uppercase tracking-[0.3em]">
              <Target className="w-3.5 h-3.5" />
              Hit Path
            </div>
            <h1
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-50 mt-2"
              data-testid="app-title"
            >
              The Workout That Works
            </h1>
            <p className="text-slate-400 mt-2 max-w-xl text-sm sm:text-base">
              The 2450 method, instant. Rolling digits, date-sum anchors, mirror substitution —
              point at where today&apos;s hit lives on the grid, in any state, any game.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Tabs value={mode} onValueChange={setMode} className="w-full sm:w-auto">
              <TabsList
                data-testid="mode-tabs"
                className="bg-[#131c2e] border border-slate-800 h-11"
              >
                <TabsTrigger
                  value="pick2"
                  data-testid="mode-pick2"
                  className="font-heading font-bold data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"
                >
                  2-Digit
                </TabsTrigger>
                <TabsTrigger
                  value="pick3"
                  data-testid="mode-pick3"
                  className="font-heading font-bold data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"
                >
                  3-Digit
                </TabsTrigger>
                <TabsTrigger
                  value="pick4"
                  data-testid="mode-pick4"
                  className="font-heading font-bold data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"
                >
                  4-Digit
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Link
              to="/how-it-works"
              data-testid="how-it-works-link"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-700 bg-slate-900/60 text-slate-300 text-sm font-heading font-bold hover:border-amber-400/60 hover:text-amber-300 transition"
            >
              How it works
            </Link>
            <UserMenu />
          </div>
        </header>

        {/* UPGRADE NUDGE — free users with 3+ saved workouts */}
        <UpgradeNudge user={user} workoutCount={workouts.length} />

        {/* CONTROLS */}
        <Card className="bg-[#131c2e]/90 border-slate-800 shadow-xl shadow-black/40">
          <CardContent className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="date-input" className="text-xs uppercase tracking-widest text-slate-400 font-heading">
                Draw Date
              </Label>
              <Input
                id="date-input"
                data-testid="date-picker-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-12 bg-[#0b0f17] border-slate-700 font-mono-digit text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seed-input" className="text-xs uppercase tracking-widest text-slate-400 font-heading">
                Seed Number ({seedLength} digits)
              </Label>
              <div className="relative">
                <Hash className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  id="seed-input"
                  data-testid="seed-number-input"
                  value={seedInput}
                  onChange={(e) =>
                    setSeedInput(e.target.value.replace(/\D/g, "").slice(0, seedLength))
                  }
                  placeholder={seedLength === 4 ? "2450" : "245"}
                  className="h-12 pl-9 bg-[#0b0f17] border-slate-700 font-mono-digit text-xl tracking-widest"
                />
              </div>
            </div>
            <Button
              data-testid="save-workout-btn"
              onClick={saveWorkout}
              className="h-12 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-heading font-bold gap-2 px-6"
            >
              <Save className="w-4 h-4" />
              Save
            </Button>
          </CardContent>
        </Card>

        {/* STEP 2 - DATE SUM */}
        <DateSumPanel breakdown={dateSum} />

        {/* COVERAGE STRIP */}
        <div
          data-testid="coverage-strip"
          className="rounded-xl border border-slate-800 bg-[#131c2e]/70 backdrop-blur-md px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div className="text-xs uppercase tracking-widest text-slate-400 font-heading">
            Workout coverage for seed <span className="text-amber-300 font-mono-digit">{seedDigits.join("")}</span>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <div className="font-mono-digit text-2xl sm:text-3xl font-extrabold text-cyan-300">
                {coverage.two.toLocaleString()}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">2-digit reachable</div>
            </div>
            <div className="w-px h-10 bg-slate-800" />
            <div>
              <div className="font-mono-digit text-2xl sm:text-3xl font-extrabold text-emerald-300">
                {coverage.three.toLocaleString()}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">3-digit reachable</div>
            </div>
            <div className="w-px h-10 bg-slate-800" />
            <div>
              <div className="font-mono-digit text-2xl sm:text-3xl font-extrabold text-amber-300">
                {coverage.four.toLocaleString()}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">4-digit reachable</div>
            </div>
          </div>
        </div>

        {/* STEP 1 - ROLLING WORKOUT */}
        <Card className="bg-[#131c2e]/90 border-slate-800 shadow-xl shadow-black/40">
          <CardContent className="p-5 sm:p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-100">
                Step 1 · Rolling Workout
              </h3>
              <span className="text-[11px] uppercase tracking-widest text-slate-500 font-heading">
                Horizontal · Vertical · Diagonal · starts & ends on seed
              </span>
            </div>
            <RollingWorkout
              columns={columns}
              mirrorFlags={mirrorFlags}
              onToggleCell={toggleCell}
              dateSumDigit={dateSum.sumDigit}
              anchorCells={candidates.anchorCells}
            />
          </CardContent>
        </Card>

        {/* STEP 3 - MIRROR */}
        <MirrorPanel
          activePair={activePair}
          onSelectPair={(p) => setActivePair((prev) => (prev === p ? null : p))}
          globalMirrorOn={globalMirrorOn}
          onToggleGlobal={() => setGlobalMirrorOn((v) => !v)}
        />

        {/* HITS */}
        <HitsPanel
          hits={candidates.hits}
          anchorCount={candidates.anchorCells.length}
          anchorDigits={candidates.anchorDigits}
          targetLength={targetLength}
          activePair={activePair}
          globalMirrorOn={globalMirrorOn}
          starred={starred}
          onToggleStar={toggleStar}
          luckyNumber={luckyNumber}
          onLuckyPick={doLuckyPick}
          seedDigits={seedDigits}
          dateSumDigit={dateSum.sumDigit}
        />

        {/* HISTORY */}
        <Tabs defaultValue="history">
          <TabsList className="bg-[#131c2e] border border-slate-800">
            <TabsTrigger
              value="history"
              data-testid="tab-history"
              className="font-heading data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"
            >
              History
            </TabsTrigger>
          </TabsList>
          <TabsContent value="history" className="mt-4">
            <HistoryPanel
              workouts={workouts}
              onOpen={openWorkout}
              onDelete={deleteWorkout}
              onSetDraw={setDraw}
            />
          </TabsContent>
        </Tabs>

        <footer className="pt-6 pb-10 text-center text-xs text-slate-600 font-mono-digit space-y-2">
          <div>
            © {new Date().getFullYear()} The Jersey Fellas LLC · Hit Path™ · works for any Pick 3, Pick 4, or multi-ball game
          </div>
          <div className="text-[10px] text-slate-700 max-w-2xl mx-auto px-4 leading-relaxed">
            For entertainment only. Hit Path shows patterns from a personal number workout — it
            does not predict outcomes. Lottery draws are random; no result is guaranteed. Must be
            18 or older (21+ in some states) to play. Play responsibly. If gambling is a problem,
            call <span className="text-amber-500">1-800-GAMBLER</span>.
          </div>
          <div className="flex items-center justify-center gap-3 text-slate-700">
            <a href="/terms" className="hover:text-slate-400 transition-colors" data-testid="footer-terms">Terms</a>
            <span>·</span>
            <a href="/privacy" className="hover:text-slate-400 transition-colors" data-testid="footer-privacy">Privacy</a>
            <span>·</span>
            <a href="mailto:support@thejerseyfellas.com" className="hover:text-slate-400 transition-colors" data-testid="footer-support">support@thejerseyfellas.com</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
